import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const navItems = [
  { to: '/admin',              label: 'Dashboard',      icon: 'dashboard',          end: true  },
  { to: '/admin/products',     label: 'Products',       icon: 'inventory_2'                    },
  { to: '/admin/categories',   label: 'Categories',     icon: 'category'                       },
  { to: '/admin/dealers',      label: 'Dealers',        icon: 'handshake'                      },
  { to: '/admin/inquiries',    label: 'Inquiries',      icon: 'receipt_long'                   },
  { to: '/admin/price-lists',  label: 'Price Lists',    icon: 'picture_as_pdf'                 },
  { to: '/admin/measure-guides',label:'Measure Guides', icon: 'straighten'                     },
  { to: '/admin/feedback',     label: 'Feedback',       icon: 'feedback'                       },
  { to: '/admin/content',      label: 'Site Content',   icon: 'edit_note'                      },
  { to: '/admin/users',        label: 'Users',          icon: 'group'                          },
  { to: '/admin/certificates', label: 'Certificates',   icon: 'workspace_premium'              },
  { to: '/admin/stock-groups', label: 'Current Stock',  icon: 'table_chart'                    },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen flex bg-surface">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-on-surface z-40
        transition-transform duration-300 flex flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-headline font-black text-lg text-primary-container uppercase tracking-tight leading-none">Central Fasteners</p>
              <p className="font-label text-[10px] text-gray-500 uppercase tracking-[0.2em] mt-0.5">Admin Panel</p>
            </div>
            <button className="md:hidden text-gray-500 hover:text-white" onClick={() => setSidebarOpen(false)}>
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-0.5">
          {navItems.map(({ to, label, icon, end }) => (
            <NavLink key={to} to={to} end={end}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 font-label text-sm font-semibold transition-all duration-150 ${
                  isActive
                    ? 'bg-primary-container text-white'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`
              }>
              <span className="material-symbols-outlined text-[20px]">{icon}</span>
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <div className="w-8 h-8 bg-primary-container flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-[16px]">admin_panel_settings</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-label text-sm font-bold text-white truncate">{user?.name}</p>
              <p className="font-label text-[10px] text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-2 font-label text-xs text-gray-500 hover:text-red-400 transition-colors">
            <span className="material-symbols-outlined text-[16px]">logout</span> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-outline-variant/30 flex items-center px-6 gap-4 sticky top-0 z-20">
          <button className="md:hidden text-secondary" onClick={() => setSidebarOpen(true)}>
            <span className="material-symbols-outlined">menu</span>
          </button>
          <div className="flex-1" />
          <a href="/" target="_blank" rel="noreferrer"
            className="flex items-center gap-2 font-label text-xs text-secondary hover:text-primary-container transition-colors">
            <span className="material-symbols-outlined text-[16px]">open_in_new</span>
            View Site
          </a>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
